// useLivePreview (#262) — drives the Looks editor's live camera preview. Opens the camera's /live twin
// as a relay popup (which postMessages raw JPEG frames), dithers each frame in a Web Worker (the shared
// cores/liveDither core in POST-BACK mode, so it's byte-identical to the camera), and posts the dithered
// frames BACK as ImageBitmaps. The caller's onPaint draws each onto the existing Dither·Colour / Look orb.
// Backpressure keeps exactly one frame in flight (freshest, lowest latency).
import { useCallback, useEffect, useRef, useState } from "react";
import { buildRelayUrl, parseRelayMessage } from "./liveRelay";
import type { EditableLook } from "./LookPortal";

export type LiveState = "idle" | "connecting" | "live" | "error";

export interface LivePreviewOpts {
  /** Called per dithered frame the worker posts back — drawImage the bitmap onto the matching orb. */
  onPaint: (role: "look" | "dither", bmp: ImageBitmap) => void;
}

export interface LivePreview {
  state: LiveState;
  detail: string;
  fps: number;
  kbps: number;
  /** Open the relay popup at `quality` (stream preset 0-3) + start. Must run in a click handler. */
  connect: (host: string, quality: number) => void;
  disconnect: () => void;
  setLook: (look: EditableLook) => void;
  /** The Dither·Colour reference Look (or null to skip that orb). */
  setRef: (ref: EditableLook | null) => void;
  setGrid: (grid: number) => void;
}

export function useLivePreview({ onPaint }: LivePreviewOpts): LivePreview {
  const [state, setState] = useState<LiveState>("idle");
  const [detail, setDetail] = useState("");
  const [fps, setFps] = useState(0);
  const [kbps, setKbps] = useState(0);

  const worker = useRef<Worker | null>(null);
  const popup = useRef<Window | null>(null);
  const origin = useRef("");
  const busy = useRef(false);
  const pending = useRef<ArrayBuffer | null>(null);
  const frames = useRef(0);
  const bytes = useRef(0);
  const last = useRef(0);
  const poll = useRef<number | null>(null);
  const onMsg = useRef<((e: MessageEvent) => void) | null>(null);
  // No-frame watchdog: a camera that's asleep / not in Stream mode / a typo'd host loads the
  // browser's error page in the cross-origin popup — it never closes and never postMessages,
  // so without a timeout "connecting…" spun forever with a stranded popup.
  const watchdog = useRef<number | null>(null);
  const sawFrame = useRef(false);
  // Latest look/ref/grid so connect() can seed a freshly-spawned worker before frames arrive.
  const lookRef = useRef<EditableLook | null>(null);
  const refRef = useRef<EditableLook | null>(null);
  const gridRef = useRef(240);
  const onPaintRef = useRef(onPaint);
  onPaintRef.current = onPaint;

  const feed = useCallback(() => {
    const w = worker.current;
    if (!w || busy.current || !pending.current) return;
    busy.current = true;
    const b = pending.current;
    pending.current = null;
    w.postMessage({ type: "frame", buf: b }, [b]);
  }, []);

  const disconnect = useCallback(() => {
    if (watchdog.current !== null) { clearTimeout(watchdog.current); watchdog.current = null; }
    if (poll.current !== null) { clearInterval(poll.current); poll.current = null; }
    if (onMsg.current) { window.removeEventListener("message", onMsg.current); onMsg.current = null; }
    worker.current?.terminate(); worker.current = null;
    try { popup.current?.close(); } catch { /* closing a cross-origin popup can throw */ }
    popup.current = null;
    busy.current = false; pending.current = null;
    setState("idle"); setDetail(""); setFps(0); setKbps(0);
  }, []);

  const connect = useCallback((host: string, quality: number) => {
    disconnect();
    const url = buildRelayUrl(host, window.location.origin, quality);
    if (!url) { setState("error"); setDetail("invalid camera address"); return; }
    origin.current = new URL(url).origin;

    const win = window.open(url, "pixie-live-relay", "width=480,height=360");
    if (!win) { setState("error"); setDetail("popup blocked — allow popups for the editor, then retry"); return; }
    popup.current = win;

    let w: Worker;
    try {
      w = new Worker(new URL("./dither.worker.ts", import.meta.url), { type: "module" });
    } catch {
      setState("error"); setDetail("this editor needs a recent Chrome (OffscreenCanvas + Worker)");
      win.close(); popup.current = null; return;
    }
    worker.current = w;
    w.postMessage({ type: "init", postback: true });
    if (lookRef.current) w.postMessage({ type: "look", look: lookRef.current });
    w.postMessage({ type: "ref", ref: refRef.current });
    w.postMessage({ type: "grid", w: gridRef.current });

    frames.current = 0; bytes.current = 0; last.current = performance.now();
    w.onmessage = (e: MessageEvent) => {
      const d = e.data as { type?: string; role?: "look" | "dither"; bitmap?: ImageBitmap; ok?: boolean; bytes?: number };
      if (d.type === "paint" && d.bitmap && d.role) { onPaintRef.current(d.role, d.bitmap); return; }
      // "stat" (one per frame) — clear backpressure + update the meter + feed the next.
      busy.current = false;
      if (d.ok) {
        setState("live");
        frames.current++;
        bytes.current += d.bytes ?? 0;
        const t = performance.now();
        const dt = t - last.current;
        if (dt >= 1000) {
          setFps(Math.round((frames.current * 1000) / dt));
          setKbps(Math.round(bytes.current / dt));
          frames.current = 0; bytes.current = 0; last.current = t;
        }
      }
      feed();
    };
    w.onerror = () => { disconnect(); setState("error"); setDetail("live preview worker error"); }; // full teardown, not just state

    const listener = (e: MessageEvent) => {
      if (e.origin !== origin.current) return; // only trust the connected camera popup
      const msg = parseRelayMessage(e.data);
      if (!msg) return;
      if (msg.type === "error") { setState("error"); setDetail("camera stream error — is it in Stream mode?"); return; }
      if (msg.type === "frame") {
        if (!sawFrame.current) {
          sawFrame.current = true;
          if (watchdog.current !== null) { clearTimeout(watchdog.current); watchdog.current = null; }
        }
        pending.current = msg.buf; feed();
      }
      // "ready" — flip to "live" on the first painted frame instead
    };
    onMsg.current = listener;
    window.addEventListener("message", listener);

    setState("connecting"); setDetail("connecting to " + origin.current + "…");
    poll.current = window.setInterval(() => {
      if (popup.current?.closed) { setDetail("live window closed"); disconnect(); }
    }, 1500);
    // Arm the no-frame watchdog: 10s with zero frames ⇒ tear everything down (incl. the
    // stranded popup) and say what to actually check. The popup-closed detail branch above
    // wins if the user closed it themselves (disconnect() clears this timer).
    sawFrame.current = false;
    const target = origin.current;
    watchdog.current = window.setTimeout(() => {
      if (sawFrame.current) return;
      disconnect();
      setState("error");
      setDetail(`no frames from ${target} after 10s — is the camera awake and in Stream mode? Check the address.`);
    }, 10_000);
  }, [disconnect, feed]);

  const setLook = useCallback((look: EditableLook) => {
    lookRef.current = look;
    worker.current?.postMessage({ type: "look", look });
  }, []);
  const setRef = useCallback((ref: EditableLook | null) => {
    refRef.current = ref;
    worker.current?.postMessage({ type: "ref", ref });
  }, []);
  const setGrid = useCallback((grid: number) => {
    gridRef.current = grid;
    worker.current?.postMessage({ type: "grid", w: grid });
  }, []);

  useEffect(() => disconnect, [disconnect]); // tear down on unmount

  return { state, detail, fps, kbps, connect, disconnect, setLook, setRef, setGrid };
}
